import { useEffect, useState, useRef } from 'react';
import { getValue } from 'firebase/remote-config';
import { remoteConfig } from '../lib/firebase';
import { TabId } from './TabBar';
import log from '../utils/logger';

interface PromoBannerProps {
  selectedTab: TabId;
  onBannerClick?: (url: string) => void;
}

export default function PromoBanner({ selectedTab, onBannerClick }: PromoBannerProps) {
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const [bannerHeight, setBannerHeight] = useState<number>(0);
  const [currentPageId, setCurrentPageId] = useState<string>('');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Listen for messages from the iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      log.debug("Received message from iframe:", event.data);
      
      if (event.data && event.data.type === 'bannerHeight' && event.data.data) {
        const heightsData = event.data.data;
        log.debug("Current pageId:", currentPageId);
        
        // Extract height for current pageId, default to 0
        const height = heightsData[currentPageId] || 0;
        log.debug("Setting banner height to:", height);
        setBannerHeight(height);
      }
      
      if (event.data && event.data.type === 'bannerClick' && event.data.url) {
        log.debug("Banner clicked, URL:", event.data.url);
        if (onBannerClick) {
          onBannerClick(event.data.url);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [currentPageId, onBannerClick]);

  useEffect(() => {
    // Get the base URL and section-specific pageID from remote config
    const promoBannerURL = getValue(remoteConfig, 'promoBannerURL').asString();
    
    let pageId = '';
    switch (selectedTab) {
      case TabId.SLOT:
        pageId = getValue(remoteConfig, 'promoBannerSlot').asString();
        break;
      case TabId.CASINO:
        pageId = getValue(remoteConfig, 'promoBannerCasino').asString();
        break;
      case TabId.CASINO_LIVE:
        pageId = getValue(remoteConfig, 'promoBannerCasinoLive').asString();
        break;
      default:
        return;
    }

    // Only show banner if both URL and pageId are available
    if (promoBannerURL && pageId) {
      // Replace $pageID placeholder with the actual pageId
      let finalUrl = promoBannerURL.replace('$pageID', pageId);
      
      // Add isIframe=true parameter
      const url = new URL(finalUrl);
      url.searchParams.set('isIframe', 'true');
      finalUrl = url.toString();
      
      setBannerUrl(finalUrl);
      setCurrentPageId(pageId);
      log.debug('PromoBanner URL:', finalUrl);
    } else {
      setBannerUrl(null);
      setCurrentPageId('');
    }
  }, [selectedTab]);


  if (!bannerUrl) {
    return null;
  }

  return (
    <div 
      className="px-6 pt-6 pb-2"
      style={{ display: bannerHeight > 0 ? 'block' : 'none' }}
    >
      <div 
        className="w-full overflow-hidden transition-all duration-300 rounded-lg"
        style={{ height: `${bannerHeight}px` }}
      >
        <iframe
          ref={iframeRef}
          src={bannerUrl}
          className="w-full border-0 rounded-lg"
          style={{ 
            height: `${bannerHeight}px`
          }}
          title={`Promo Banner ${selectedTab}`}
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-storage-access-by-user-activation"
        />
      </div>
    </div>
  );
}